import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { checkConsent as apiCheckConsent, createConsent as apiCreateConsent } from '../services/consentService';

// 'idle' | 'loading' | 'valid' | 'required'
const ConsentContext = createContext(null);

export function ConsentProvider({ children }) {
  const [status, setStatus] = useState('idle');
  const [suppressed, setSuppressed] = useState(true);

  // Refs to track state inside callbacks without causing re-renders or stale closures
  const statusRef = useRef('idle');
  const checkedGlid = useRef(null); // glid for which check has already completed

  const updateStatus = (val) => {
    statusRef.current = val;
    setStatus(val);
  };

  const isConsentValid = (response) => {
    if (!response || response.CODE !== '200') return false;
    const result = response.RESULT;
    if (!result || result.success === false) return false;
    return result.has_consent === true;
  };

  // Stable function — uses refs so no deps needed, never causes useEffect loops
  const checkUserConsent = useCallback(async (glid, ak) => {
    // Already verified for this glid this session — skip
    if (checkedGlid.current === glid && statusRef.current !== 'idle') return;
    // In-flight or already valid — skip
    if (statusRef.current === 'loading' || statusRef.current === 'valid') return;

    updateStatus('loading');

    const response = await apiCheckConsent(glid, ak);

    if (response === null) {
      // Network/API error — don't block the user, reset to idle
      updateStatus('idle');
      return;
    }

    // Mark this glid as checked so we never repeat the network call
    checkedGlid.current = glid;

    if (isConsentValid(response)) {
      updateStatus('valid');
    } else {
      updateStatus('required');
    }
  }, []); // empty deps — stable across renders

  // Mark consent as granted locally without a network call.
  // Used when consent is recorded out-of-band (e.g. initiate-bureau records
  // consent on the backend), so the cached 'required' state must be refreshed
  // to avoid showing the consent modal on the next consent page.
  const markConsentGranted = useCallback((glid) => {
    if (!glid) return;
    checkedGlid.current = glid;
    updateStatus('valid');
  }, []);

  // Listen for out-of-band consent grants (initiate-bureau records consent on the
  // backend). This keeps the cache in sync no matter which component triggers the
  // bureau call, so the fix lives in one place instead of every call site.
  useEffect(() => {
    const handler = (e) => markConsentGranted(e?.detail?.glid);
    window.addEventListener('ht:consent-granted', handler);
    return () => window.removeEventListener('ht:consent-granted', handler);
  }, [markConsentGranted]);

  const submitConsent = useCallback(async (consentData) => {
    const response = await apiCreateConsent(consentData);
    if (response && response.CODE === '200') {
      checkedGlid.current = consentData.glid;
      updateStatus('valid');
      return true;
    }
    return false;
  }, []);

  const value = {
    consentStatus: status,
    showConsentModal: status === 'required' && !suppressed,
    suppressConsentModal: setSuppressed,
    checkUserConsent,
    submitConsent,
  };

  return (
    <ConsentContext.Provider value={value}>
      {children}
    </ConsentContext.Provider>
  );
}

export function useConsent() {
  const ctx = useContext(ConsentContext);
  if (!ctx) throw new Error('useConsent must be used within <ConsentProvider>');
  return ctx;
}
